import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { Upload5, Trash2, ArrowRefresh2, File, Calendar2, ChevronDown2 } from 'blaze-ui/icons'
import { Pdf, Markdown, TextFile } from 'blaze-ui/icons/24'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { Form, Formik } from 'formik'
import { useCallback, useState, useRef } from 'react'
import { FormikHelpers } from 'formik/dist/types'
import Styles from './CampaignDetails.module.scss'
import { CampaignDetailsProps, FormValues } from './Types'
import * as yup from 'yup'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Input } from 'almanac-ui/TextInputField/Input'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { collectResults } from 'utils/query'
import { useFileUpload } from 'components/FirstTimeExperienceModal/steps/BrandProfileDocuments/useFileUpload'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { useGenerateCampaignInputs } from 'hooks/queries/brandPlans/useGenerateCampaignInputs'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { getLocalTimeZone, today } from '@internationalized/date'
import dayjs from 'dayjs'
import { Heading } from 'blaze-ui/Heading/Heading'

const MAX_FILES = 10

const validationSchema = yup.object().shape({
  growth_goals_input: yup.string(),
  key_dates_input: yup.string(),
})

const getFileIcon = (doc: any) => {
  const mimeType = doc.source?.mime_type

  if (mimeType === 'application/pdf') {
    return <Pdf size={20} color="var(--red-50)" />
  }
  if (mimeType === 'text/markdown') {
    return <Markdown size={20} color="var(--green-50)" />
  }
  if (mimeType === 'text/plain') {
    return <TextFile size={20} color="var(--teal-50)" />
  }
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <File size={20} color="var(--blue-50)" />
  }

  return <File size={20} color="var(--dark-80)" />
}

const getUploadButtonText = (isUploading: boolean, fileCount: number) => {
  if (isUploading) return 'Uploading...'
  if (fileCount >= MAX_FILES) return 'Max 10 files'
  if (fileCount > 0) return 'Upload more'
  return 'Upload your brand strategy docs'
}

const CampaignDetailsStep = (props: CampaignDetailsProps) => {
  const { onBack, onNext, initialValues, brandPlanId, brandKitId } = props
  const { isMobile } = useResponsiveDesign()

  const { mutate: generateGoals, isLoading: isGeneratingGoals } = useGenerateCampaignInputs(brandPlanId)
  const { mutate: generateDates, isLoading: isGeneratingDates } = useGenerateCampaignInputs(brandPlanId)
  const [isGeneratingBoth, setIsGeneratingBoth] = useState(false)
  const hasInitialGeneratedRef = useRef(false)
  const [startDate, setStartDate] = useState<string>(initialValues?.startDate || dayjs().add(1, 'day').toISOString())

  const { handleUpload, isLoading: isUploading } = useFileUpload(brandKitId)

  const documentsQuery = useBrandKitSources(
    {
      brandKitId: brandKitId,
      sourceType: ['SourceMaterialsMediaObject'],
    },
    {
      enabled: !!brandKitId,
      refetchOnWindowFocus: false,
    },
  )
  const documents = collectResults(documentsQuery, (data) => data.brand_kit_sources) || []

  const { mutate: deleteSource } = useDeleteBrandKitSource({
    onSuccess: () => {
      documentsQuery.refetch()
    },
  })

  const handleGenerateGoals = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
        },
      })
    },
    [generateGoals],
  )

  const handleGenerateDates = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateDates(undefined, {
        onSuccess: (data) => {
          setFieldValue('key_dates_input', data.key_dates_input)
        },
      })
    },
    [generateDates],
  )

  const handleGenerateBoth = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      setIsGeneratingBoth(true)

      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
          setFieldValue('key_dates_input', data.key_dates_input)
          setIsGeneratingBoth(false)
        },
        onError: () => {
          setIsGeneratingBoth(false)
        },
      })
    },
    [generateGoals],
  )

  const handleFilesSelected = useCallback(
    async (fileList: FileList | null, setFieldValue?: (field: string, value: any) => void) => {
      if (!fileList || !brandKitId) return

      const currentCount = documents.length
      const availableSlots = Math.max(0, MAX_FILES - currentCount)

      if (availableSlots === 0) {
        showErrorMessageToast(`You can upload a maximum of ${MAX_FILES} files.`)
        return
      }

      const files = Array.from(fileList).slice(0, availableSlots)

      if (fileList.length > availableSlots) {
        showErrorMessageToast(
          `Only ${availableSlots} file${availableSlots > 1 ? 's' : ''} can be added. Maximum ${MAX_FILES} files total.`,
        )
      }

      await handleUpload(files)
      await documentsQuery.refetch()

      if (setFieldValue) {
        handleGenerateBoth(setFieldValue)
      }
    },
    [brandKitId, handleUpload, documentsQuery, documents.length, handleGenerateBoth],
  )

  const handleDeleteDocument = useCallback(
    (sourceId: number) => {
      if (!brandKitId) return
      deleteSource({ brandKitId, id: sourceId })
    },
    [brandKitId, deleteSource],
  )

  const handleSubmit = useCallback(
    async ({ growth_goals_input, key_dates_input }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      try {
        onNext({ growth_goals_input, key_dates_input, startDate })
      } catch (err) {
        showErrorMessageToast('Failed to proceed, please try again.')
        setSubmitting(false)
      }
    },
    [onNext, startDate],
  )

  return (
    <MultiStepStepLayout
      title="Review your content strategy"
      description="Answer these questions to help us build a 12-month content plan aligned with your business goals and key moments."
      scrollContainerClassName={Styles.scrollContainer}
    >
      <Formik
        initialValues={{
          growth_goals_input: initialValues?.growth_goals_input || '',
          key_dates_input: initialValues?.key_dates_input || '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, errors, touched, submitForm, isSubmitting, setFieldValue }) => {
          // Trigger initial generation once when component mounts with empty fields
          if (
            !hasInitialGeneratedRef.current &&
            !values.growth_goals_input &&
            !values.key_dates_input &&
            !isGeneratingGoals &&
            !isGeneratingDates &&
            brandPlanId
          ) {
            hasInitialGeneratedRef.current = true
            handleGenerateBoth(setFieldValue)
          }

          return (
            <StickyFooterProvider
              footer={
                <StickyFooter>
                  <FooterButtons>
                    <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back" isDisabled={isSubmitting}>
                      Back
                    </Button>
                    <Button size="lg" onPress={submitForm} aria-label="Generate my campaigns" isDisabled={isSubmitting}>
                      Generate my campaigns
                    </Button>
                  </FooterButtons>
                </StickyFooter>
              }
            >
              <Form className={Styles.form}>
                <div className={Styles.content}>
                  {brandKitId && (
                    <div className={Styles.documentsSection}>
                      <div className={Styles.documentsHeading}>
                        <div className={Styles.headingRow}>
                          <Heading level={3}>Add strategy documents</Heading>
                          <span className={Styles.optionalBadge}>
                            <Text variant="secondary" color="var(--dark-80)">
                              Optional
                            </Text>
                          </span>
                        </div>
                        <Text variant="secondary" color="var(--dark-40)">
                          Upload brand guidelines or marketing plans to enhance Blaze's understanding of your goals.
                        </Text>
                      </div>

                      {documents.length > 0 && (
                        <div className={Styles.documentsList}>
                          {documents.map((doc) => (
                            <div key={doc.id} className={Styles.documentRow}>
                              <div className={Styles.documentInfo}>
                                {getFileIcon(doc)}
                                <Text variant="secondary" color="var(--dark-80)" lineClamp={1}>
                                  {doc.name || 'Document'}
                                </Text>
                              </div>
                              <IconButton
                                icon={Trash2}
                                variant="tertiary"
                                size="lg"
                                onPress={() => handleDeleteDocument(doc.id)}
                                aria-label="Delete file"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className={Styles.documentButtons}>
                        <FileTrigger
                          onSelect={(files) => handleFilesSelected(files, setFieldValue)}
                          accept=".pdf,.doc,.docx,.txt,.md"
                          allowsMultiple={true}
                          aria-label="Upload Files"
                        >
                          <Button
                            variant="secondary"
                            size={isMobile ? 'lg' : 'xl'}
                            frontIcon={Upload5}
                            fullWidth={isMobile}
                            isDisabled={isUploading || documentsQuery.isLoading || documents.length >= MAX_FILES}
                          >
                            {getUploadButtonText(isUploading, documents.length)}
                          </Button>
                        </FileTrigger>
                      </div>
                    </div>
                  )}

                  <div className={Styles.field}>
                    <div className={Styles.fieldHeader}>
                      <Heading level={3}>What are your key growth goals for the next 12 months?</Heading>
                      <div className={Styles.helperRow}>
                        <Text variant="secondary" color="var(--dark-40)">
                          AI-generated based on your business. Click to edit or regenerate.
                        </Text>
                        <Button
                          variant="tertiary"
                          size="sm"
                          frontIcon={ArrowRefresh2}
                          onPress={() => handleGenerateGoals(setFieldValue)}
                          isDisabled={isGeneratingGoals || isGeneratingBoth}
                        >
                          Regenerate
                        </Button>
                      </div>
                    </div>
                    {isGeneratingGoals || isGeneratingBoth ? (
                      <TextFieldLoadingState lines={4} />
                    ) : (
                      <Input
                        id="growth_goals_input"
                        name="growth_goals_input"
                        value={values.growth_goals_input}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="e.g. Double revenue; Launch a new product..."
                        isTextarea
                        isValid={!errors.growth_goals_input}
                        errorMessage={touched.growth_goals_input ? errors.growth_goals_input : undefined}
                      />
                    )}
                  </div>

                  <div className={Styles.field}>
                    <div className={Styles.fieldHeader}>
                      <Heading level={3}>What are key dates or milestones for your business?</Heading>
                      <div className={Styles.helperRow}>
                        <Text variant="secondary" color="var(--dark-40)">
                          AI-generated. Click to edit or regenerate.
                        </Text>
                        <Button
                          variant="tertiary"
                          size="sm"
                          frontIcon={ArrowRefresh2}
                          onPress={() => handleGenerateDates(setFieldValue)}
                          isDisabled={isGeneratingDates || isGeneratingBoth}
                        >
                          Regenerate
                        </Button>
                      </div>
                    </div>
                    {isGeneratingDates || isGeneratingBoth ? (
                      <TextFieldLoadingState lines={4} />
                    ) : (
                      <Input
                        id="key_dates_input"
                        name="key_dates_input"
                        value={values.key_dates_input}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="e.g. September 12th is our product launch, Black Friday is huge for us, tax filing day is April 15th..."
                        isTextarea
                        isValid={!errors.key_dates_input}
                        errorMessage={touched.key_dates_input ? errors.key_dates_input : undefined}
                      />
                    )}
                  </div>

                  <div className={Styles.datePickerRow}>
                    <Heading level={3}>When should your first campaign start?</Heading>
                    <DateTimePicker
                      disableDatesBefore={today(getLocalTimeZone()).add({ days: 1 }).toString()}
                      dateTime={dayjs(startDate)}
                      setDateTime={(date) => {
                        if (!date) return
                        setStartDate(date.format())
                      }}
                      showTimeControls={false}
                    >
                      <Button frontIcon={Calendar2} size="xl" variant="secondary" endIcon={ChevronDown2}>
                        {dayjs(startDate).format('dddd, MMM D')}
                      </Button>
                    </DateTimePicker>
                  </div>
                </div>
              </Form>
            </StickyFooterProvider>
          )
        }}
      </Formik>
    </MultiStepStepLayout>
  )
}

export { CampaignDetailsStep as CampaignDetails }
